import { Component, Inject, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { LoaderService } from './core/loader/loader';
import { ThemeService } from './shared/services/theme.service';

const ID_TOKEN_KEY = 'app_id_token';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  standalone: false,
})
export class App {
  protected readonly title = signal('QC');
  public initials: string = '';
  public appStarter: boolean = true; // Show loader initially
  public sidebarVisible: boolean = false;
  public loading: boolean = false;

  public readonly dialog: MatDialog = inject(MatDialog);
  private readonly themeService: ThemeService = inject(ThemeService);

  loginDisplay = false;
  tokenExpiration: string = '';
  private readonly _destroying$ = new Subject<void>();
  private msalInitialized = false; // track MSAL initialization

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    // Handle redirect and capture token
    this.authService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult) => {
        if (result?.idToken) {
          console.log('Redirect handled - idToken captured');
          localStorage.setItem(ID_TOKEN_KEY, result.idToken);
        }
      },
      error: (error) => console.error('Redirect error:', error)
    });

    // Wait for MSAL to finish redirect or login interactions
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.msalInitialized = true; // mark MSAL as ready
        this.setLoginDisplay();
        this.appStarter = false; // hide loader
      });

    // Listen for token events (LOGIN_SUCCESS and ACQUIRE_TOKEN_SUCCESS)
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => 
          msg.eventType === EventType.LOGIN_SUCCESS ||
          msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((msg) => {
        const payload = msg.payload as AuthenticationResult;
        if (payload?.idToken) {
          console.log('Token event - idToken captured');
          localStorage.setItem(ID_TOKEN_KEY, payload.idToken);
        }
        if (payload?.expiresOn) {
          this.tokenExpiration = payload.expiresOn.toString();
          localStorage.setItem('tokenExpiration', this.tokenExpiration);
        }
      });

    this.loaderService.loaderState$.subscribe((state) => {
      this.loading = state;
    });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  login() {
    // Only trigger login if MSAL has initialized and no accounts exist
    if (this.msalInitialized && this.authService.instance.getAllAccounts().length === 0) {
      if (this.msalGuardConfig.authRequest) {
        this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        this.authService.loginRedirect();
      }
    }
  }

  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
